import { t } from "@/language/translation";
import { type InjectionKey, type Ref } from "vue";
import {
  ChartCalculationType,
  ChartDiagramType,
  ChartInterval,
  ChartTimeFrameStartFormats,
  ChartTypes,
  ValueDisplayMethod,
} from "@/graphql";
import { type ChartEditType, SeriesModalViewTypes } from "./chartTypes";
import defaultDataSeries from "./functions/DefaultDataSeries";

export const ChartLayoutOptionsProvideKey: InjectionKey<Ref<ChartLayoutOptionsType>> = Symbol(
  "chartLayoutOptionsProvideKey",
);

export const ChartSeriesNewProvideKey: InjectionKey<Ref<boolean>> = Symbol(
  "chartSeriesNewProvideKey",
);

type AllOptionalChartLayoutOptionsType = {
  // #region calculation
  hideAddButtons: boolean;
  disableDataGranularity: boolean;
  disableDeleteSeriesButton: boolean;
  hideAggregationOnSeriesModal: boolean;
  hideDiagramTypeSelect: boolean;
  hideVisibleButtons: boolean;
  hideInterpolateOnDatapointsSeriesModal: boolean;
  // #endregion
  // #region previous data
  hidePreviousDataTab: boolean;
  // #endregion
  // #region layout
  disableGeneralArea: boolean;
  disableXTimeFrame: boolean;
  disableSecondaryYAxis: boolean;
  hideYAxis: boolean;
  hideYAxisOptics: boolean;
  showYCenterAxisSettings: boolean;
  showGlobalType: boolean;
  hideAxisName: boolean;
  hideLineConnection: boolean;
  hideColumnValue: boolean;
  showLegendOptions: boolean;
  // #endregion
  // #region additional plots
  hideAdditionalPlotTab: boolean;
  // #endregion
  // #region series connection
  hideSeriesConnectionTab: boolean;
  // #endregion
  // #region data input
  hideDataInputTab: boolean;
  // #endregion
  // #region summery
  hideSummaryTab: boolean;
  // #endregion
};

type AllNonOptionalChartLayoutOptionsType = {
  addDefaultValuesToChartSettings: (chartSettings: ChartEditType) => ChartEditType;
  maxTabs: number;
};

export type ChartLayoutOptionsType = Partial<AllOptionalChartLayoutOptionsType> &
  AllNonOptionalChartLayoutOptionsType;

const commonTargetAndPPSOptions: Partial<AllOptionalChartLayoutOptionsType> = {
  hideSeriesConnectionTab: true,
  hideDataInputTab: true,
  hideAdditionalPlotTab: true,
  hideAddButtons: true,
  hidePreviousDataTab: true,
  disableDataGranularity: true,
  disableDeleteSeriesButton: true,
  hideAggregationOnSeriesModal: true,
  disableGeneralArea: true,
  disableXTimeFrame: true,
  disableSecondaryYAxis: true,
  hideDiagramTypeSelect: true,
  hideVisibleButtons: true,
  hideSummaryTab: true,
  hideInterpolateOnDatapointsSeriesModal: true,
};

const addDefaultValuesToChartSettingsForTargetAndPPS = (
  chartSettings: ChartEditType,
): ChartEditType => {
  chartSettings.dataGranularity = ChartInterval.SHIFT;
  chartSettings.previousDataSettings.dataGranularity = ChartInterval.MONTH;
  chartSettings.globalSettings.timeFrame = ChartInterval.WEEK;
  chartSettings.globalSettings.timeFrameStart = ChartTimeFrameStartFormats.BACKWARDS;
  chartSettings.globalSettings.timeFrameCount = 1;
  chartSettings.globalSettings.dataInput.dataInputActive = true;
  const datapointSeries = defaultDataSeries(
    SeriesModalViewTypes.datapoint,
    {
      aggregation: {
        type: ChartCalculationType.ADDITION,
        moving: undefined,
        interval: chartSettings.dataGranularity,
      },
      name: "",
      datapointTypeId: undefined,
      costCenterId: undefined,
      valueDisplayMethod: ValueDisplayMethod.NORMAL,
      maxCalculationDateToday: false,
    },
    chartSettings.previousDataSettings.dataGranularity,
    chartSettings,
    undefined,
  );
  datapointSeries.commonType.placeholder = "license.lisaChart.ChartLayoutOptions.normalSeries";
  chartSettings.datapoints.push(datapointSeries);
  return chartSettings;
};

const target: ChartLayoutOptionsType = {
  maxTabs: 3,
  ...commonTargetAndPPSOptions,
  addDefaultValuesToChartSettings: (chartSettingsProp) => {
    const chartSettings = addDefaultValuesToChartSettingsForTargetAndPPS(chartSettingsProp);
    const dataTargetSeries = defaultDataSeries(
      SeriesModalViewTypes.target,
      {
        aggregation: {
          type: ChartCalculationType.ADDITION,
          moving: undefined,
          interval: ChartInterval.DAY,
        },
        name: "",
        datapointTypeId: undefined,
        costCenterId: undefined,
        valueDisplayMethod: ValueDisplayMethod.NORMAL,
        maxCalculationDateToday: false,
      },
      chartSettings.previousDataSettings.dataGranularity,
      chartSettings,
      undefined,
    );
    dataTargetSeries.commonType.placeholder = "license.lisaChart.ChartLayoutOptions.targetSeries";
    dataTargetSeries.commonType.chartType = ChartDiagramType.line;
    chartSettings.targets.push(dataTargetSeries);
    chartSettings.globalSettings.timeFrameCount = 2;
    const dataCalculationSeries = defaultDataSeries(
      SeriesModalViewTypes.calculation,
      {
        aggregation: {
          type: ChartCalculationType.AVERAGE,
          moving: {
            movingInterval: ChartInterval.DAY,
            movingSteps: 60,
          },
        },
        valueDisplayMethod: ValueDisplayMethod.NORMAL,
        name: t("license.lisaChart.ChartLayoutOptions.60DayLine") ?? "",
        calculationKeys: [{ key: chartSettings.datapoints[0].commonType.chartSeriesData.key }],
        maxCalculationDateToday: false,
      },
      chartSettings.previousDataSettings.dataGranularity,
      chartSettings,
      undefined,
    );
    dataCalculationSeries.commonType.chartType = ChartDiagramType.line;
    chartSettings.calculations.push(dataCalculationSeries);

    return chartSettings;
  },
};

/**
 * default datapoint series color for feedback template
 */
export const feedbackColorScheme = [
  "#19b9ff",
  "#ffb25d",
  "#4c4ca1",
  "#f7a35c",
  "#8085e9",
  "#f15c80",
  "#e4d354",
  "#2b908f",
  "#f45b5b",
  "#91e8e1",
];

const feedback: ChartLayoutOptionsType = {
  maxTabs: 3,
  ...commonTargetAndPPSOptions,
  disableDataGranularity: false,
  addDefaultValuesToChartSettings: (chartSettingsProp) => {
    const chartSettings = addDefaultValuesToChartSettingsForTargetAndPPS(chartSettingsProp);
    chartSettings.globalSettings.timeFrame = ChartInterval.WEEK;
    chartSettings.globalSettings.timeFrameStart = ChartTimeFrameStartFormats.CURRENT;
    chartSettings.globalSettings.timeFrameCount = 2;
    chartSettings.datapoints[0].commonType.customColor = true;
    [chartSettings.datapoints[0].commonType.color] = feedbackColorScheme;
    return chartSettings;
  },
};

const pps: ChartLayoutOptionsType = {
  maxTabs: 3,
  ...commonTargetAndPPSOptions,
  addDefaultValuesToChartSettings: (chartSettingsProp) => {
    const chartSettings = addDefaultValuesToChartSettingsForTargetAndPPS(chartSettingsProp);
    const dataTargetSeries = defaultDataSeries(
      SeriesModalViewTypes.datapoint,
      {
        aggregation: {
          type: ChartCalculationType.ADDITION,
          moving: undefined,
          interval: chartSettings.dataGranularity,
        },
        name: "",
        datapointTypeId: undefined,
        costCenterId: undefined,
        valueDisplayMethod: ValueDisplayMethod.NORMAL,
        maxCalculationDateToday: false,
      },
      chartSettings.previousDataSettings.dataGranularity,
      chartSettings,
      undefined,
    );
    dataTargetSeries.commonType.placeholder = "license.lisaChart.ChartLayoutOptions.targetSeries";
    chartSettings.datapoints[0].commonType.backgroundSeriesKey =
      dataTargetSeries.commonType.chartSeriesData.key;
    dataTargetSeries.commonType.chartSeriesData.visible = false;
    dataTargetSeries.commonType.chartSeriesData.visibleBackground = true;
    chartSettings.datapoints.push(dataTargetSeries);

    const dataCalculationSeries = defaultDataSeries(
      SeriesModalViewTypes.calculation,
      {
        aggregation: {
          type: ChartCalculationType.AVERAGE,
          moving: {
            movingInterval: ChartInterval.DAY,
            movingSteps: 60,
          },
        },
        name: t("license.lisaChart.ChartLayoutOptions.60DayLine") ?? "",
        calculationKeys: [{ key: chartSettings.datapoints[0].commonType.chartSeriesData.key }],
        valueDisplayMethod: ValueDisplayMethod.NORMAL,
        maxCalculationDateToday: false,
      },
      chartSettings.previousDataSettings.dataGranularity,
      chartSettings,
      undefined,
    );
    dataCalculationSeries.commonType.chartType = ChartDiagramType.line;
    chartSettings.calculations.push(dataCalculationSeries);
    chartSettings.seriesConnection.columnOverlay = true;
    return chartSettings;
  },
};

const custom: ChartLayoutOptionsType = {
  hideAdditionalPlotTab: false,
  maxTabs: 9,
  addDefaultValuesToChartSettings: (chartSettings) => chartSettings,
};

const customCategory: ChartLayoutOptionsType = {
  hideAdditionalPlotTab: true,
  showGlobalType: true,
  hidePreviousDataTab: true,
  hideSeriesConnectionTab: true,
  hideDiagramTypeSelect: true,
  hideYAxis: false,
  showYCenterAxisSettings: true,
  hideYAxisOptics: true,
  hideDataInputTab: true,
  hideAxisName: true,
  hideLineConnection: true,
  hideColumnValue: true,
  showLegendOptions: true,
  maxTabs: 4,
  addDefaultValuesToChartSettings: (chartSettings) => {
    chartSettings.globalSettings.chartType = ChartDiagramType.pie;
    return chartSettings;
  },
};

const ChartLayoutOptions: Record<ChartTypes, ChartLayoutOptionsType> = {
  custom,
  customCategory,
  target,
  pps,
  feedback,
};

export default ChartLayoutOptions;
